// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { compose } from "recompose";
import { push } from 'connected-react-router';
import { connect } from "react-redux";
import { getI18n } from "../../utils/intl";
import { hasAdminRole } from '../../utils';
import { getUserDetails } from '../../reducers/portalConfig';
import { Clear, FiberManualRecordSharp as FiberManualRecordSharpIcon } from '@material-ui/icons';
import PropTypes from "prop-types";
import styles from "./styles";
import clsx from "clsx";
import { cloneDeep, get, isArray, isEmpty, isEqual, keys } from "lodash";
import {
  Loading,
  AlertMessages,
  FormActionButtons,
  Switch,
  FormTextField,
  Footer,
} from "../../components";
import {
  withStyles,
  Typography,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  FormControlLabel,
} from "@material-ui/core";
import {
  fetchRegistrationSettings,
  updateRegistrationSettings,
} from "../../actions/registrationSettings";
import {
  getIsLoading,
  getIsFormLoadingError,
  getRegistrationSettings,
  getUpdateErrorMsg,
  getIsFormUpdateError,
  getIsUpdateSuccessful,
} from "../../reducers/registrationSettings";
import {
  ALERT_ERROR,
  ALERT_SUCCESS,
  REGISTRATION_SETTINGS_STATUS_ENABLED,
  REGISTRATION_SETTINGS_STATUS_DISABLED,
  REGISTRATION_SETTINGS_TERMS_OF_USE_DEFAULT,
  REGISTRATION_SETTINGS_LIMIT_DISABLED,
  REGISTRATION_SETTINGS_LIMIT_ENABLED,
  REGISTRATION_SETTINGS_LIMIT_ID_DEFAULT,
  REGISTRATION_SETTINGS_TERMS_OF_USE_MAX_LENGTH,
} from "../../constants";
/* import {
  ROUTES,
} from "../../constants/routes"; */

const TYPE_THRESHOLD_ACTIVE = 'ACTIVE';
const TYPE_THRESHOLD_FIRST = 'FIRST';
const TYPE_THRESHOLD_SECOND = 'SECOND';
const TYPE_THRESHOLD_LAST = 'LAST';

export const RegistrationSettings = (props) => {
  const {
    isFormLoadingError,
    isUpdateError,
    updateErrorMsg,
    classes,
    registrationSettings,
    isLoading,
    isUpdateSuccessful,
    updateRegistrationSettings,
    user,
  } = props;

  const [registrationSettingsState, setRegistrationSettingsState] = useState(registrationSettings);
  const isFormDirty = !isEqual(registrationSettingsState, registrationSettings);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationStatus, setNotificationStatus] = useState("");

  const intl = getI18n(useIntl());
  const { getI18nMessage } = intl;
  const notifyMessages = (status, message) => {
    setNotificationStatus(status);
    setNotificationMessage(message);
  };

  useEffect(() => {
    if(!hasAdminRole(user)) {
      props.push('/404');
    }
    props.fetchRegistrationSettings();
  }, []);

  useEffect(() => {
    initForm(registrationSettings);
  }, [registrationSettings]);

  useEffect(() => {
    if (isFormLoadingError) {
      notifyMessages(ALERT_ERROR, getI18nMessage("error.settings.registrationsettings.load"));
    }
  }, [isFormLoadingError]);

  useEffect(() => {
    if(isUpdateSuccessful) {
      props.fetchRegistrationSettings();
      notifyMessages(ALERT_SUCCESS, getI18nMessage('label.settings.registrationsettings.update.success'))
      setTimeout(() => { notifyMessages('', '')}, 2500);
    }
  }, [isUpdateSuccessful]);

  useEffect(() => {
    if (isUpdateError) {
      notifyMessages(ALERT_ERROR,updateErrorMsg || getI18nMessage("error.settings.registrationsettings.update"));
      setTimeout(() => { notifyMessages('', '')}, 2500);
    }
  }, [isUpdateError]);

  const handleUnload = (evt) => {
    const unsaved_changes_warning = getI18nMessage("label.user.profile.unsaved.changes.description");
    evt.returnValue = unsaved_changes_warning;
    return unsaved_changes_warning;
  };
  useEffect(() => {
    if (isFormDirty) {
      window.addEventListener("beforeunload", handleUnload);
      return () => {
        window.removeEventListener("beforeunload", handleUnload);
      };
    }
  }, [isFormDirty]);

  const onSave = () => {
    updateRegistrationSettings(registrationSettingsState);
  };

  const onCancel = () => {
    initForm(registrationSettings);
  };

  const onRegistrationSwitchChange = (checked) => {
    setRegistrationSettingsState( {
      ...registrationSettingsState,
      "registrationStatus" : checked ? REGISTRATION_SETTINGS_STATUS_ENABLED : REGISTRATION_SETTINGS_STATUS_DISABLED,
    });
  }

  const onClickLimitUserRegistration = (event) => {
    const checked = event.target.checked;
    setRegistrationSettingsState( {
      ...registrationSettingsState,
      "userRegistrationLimitEnabled" : checked ? REGISTRATION_SETTINGS_LIMIT_ENABLED : REGISTRATION_SETTINGS_LIMIT_DISABLED,
    });
  }
  const onTermsOfUseChange = (value) => {
    setRegistrationSettingsState( {
      ...registrationSettingsState,
      "termOfUse" : value,
    });
  }
  const parseNewLine = (value) => value.replace(/(\\n)/g, "\n");

  const Setting = ({ id, title, value, handleChange, helperText }) => {
    return (<div>
      <div className={classes.switchTitleSection}>
        <Typography variant="h4" className={ classes.switchTitle }>
          {title}
        </Typography>
        <Switch
          id={id}
          checked={value}
          onChange={handleChange}
        />
      </div>
      <hr className={classes.switchHR}/>
      <Typography className={classes.switchHelperText}>
        {helperText}
      </Typography>
    </div>);
  }

  Setting.propTypes = {
    id: PropTypes.string,
    title: PropTypes.string,
    value: PropTypes.bool,
    handleChange: PropTypes.func,
    helperText: PropTypes.string,
  };

  const {
    deleteInactiveUsersAfter: deleteInactiveUsersAfterDays,
    enabled: deleteInactiveUsersAfter,
  } = registrationSettingsState;
  const [thresholdValues, setThresholdValues] = useState([]);

  const initForm = (settings) => {
    if (settings) {
      setRegistrationSettingsState(cloneDeep(settings));
      initThresholds(settings?.thresholds);
    }
  };

  const initThresholds = (thresholds) => {
    if (thresholds) {
      const fullArray = [...thresholds,
      ...(new Array(3 - thresholds.length)),
      ];
      setThresholdValues(fullArray.reverse());
    } else {
      setThresholdValues([]);
    }
  };

  const [errorsMap, setErrorsMap] = useState({});

  const handleDeleteInactiveUsers = (enabled) => {
    if (enabled) {
      setRegistrationSettingsState({
        ...registrationSettingsState,
        thresholds: [10, 30, 60],
        deleteInactiveUsersAfter: 120,
        enabled,
      });
      setThresholdValues([60, 30, 10]);
    } else {
      setRegistrationSettingsState({
        ...registrationSettingsState,
        thresholds: [],
        deleteInactiveUsersAfter: 0,
        enabled,
      });
      setThresholdValues([]);
    }
  };

  /* const UsersLink = () => <Link href={ROUTES.users}>Show Users.</Link>;
  UsersLink.propTypes = {}; */

  const handleThresholdChange = (thresholdIndex, value) => {
    thresholdValues[thresholdIndex] = value;
    setThresholdValues([...thresholdValues]);
    const allValues = [...thresholdValues].reverse();
    let index = 0;
    const updatedValues = [];
    while((allValues.length > index) && allValues[index]) {
      updatedValues.push(Number(allValues[index]));
      index++;
    }
    setRegistrationSettingsState({
      ...registrationSettingsState,
      thresholds: [...updatedValues],
    });
  };

  const getThresholdItemIcon = (type) => {
    if (type === TYPE_THRESHOLD_FIRST) {
      return classes.thresholdPrepareItemIcon;
    } else if (type === TYPE_THRESHOLD_SECOND) {
      return classes.thresholdWarnItemIcon;
    } else if (type === TYPE_THRESHOLD_LAST) {
      return classes.thresholdErrorItemIcon;
    }
    return classes.thresholdStartItemIcon;
  };

  const getThresholdIndex = (type) => {
    if (type === TYPE_THRESHOLD_FIRST) {
      return 0;
    } else if (type === TYPE_THRESHOLD_SECOND) {
      return 1;
    } else if (type === TYPE_THRESHOLD_LAST) {
      return 2;
    }
  };

  const isValidConfigNumber = (value) => Number.isInteger(Number(value)) && value > 0;

  const getThreshold = (index) => (isArray(thresholdValues) && thresholdValues.length >= index + 1)
    ? thresholdValues[index] : '';

  useEffect(() => {
    const updatedErrorsMap = {};
    if (!isEmpty(deleteInactiveUsersAfterDays)
      && !isValidConfigNumber(Number(deleteInactiveUsersAfterDays))) {
      updatedErrorsMap.inactiveDays = true;
    }
    const firstThreshold = getThreshold(0);
    const secondThresold = getThreshold(1);
    const lastThreshold = getThreshold(2);

    // if the value is specified, it must be a valid config number
    if (firstThreshold && !isValidConfigNumber(firstThreshold)) {
      updatedErrorsMap.firstThreshold = true;
    }
    // if the value is specified, it must be a valid config number
    if (secondThresold && !isValidConfigNumber(secondThresold)) {
      updatedErrorsMap.secondThresold = true;
    }
    // if the value is not specified or specified value is empty, consider it as an error
    if ((registrationSettingsState.enabled && !lastThreshold) ||
      (lastThreshold && !isValidConfigNumber(lastThreshold))) {
      updatedErrorsMap.lastThreshold = true;
    }

    const deleteInactiveUsersAfterDaysNum =
      deleteInactiveUsersAfterDays ? Number(deleteInactiveUsersAfterDays) : undefined;
    const firstThresholdNum = firstThreshold ? Number(firstThreshold) : undefined;
    const secondThresoldNum = secondThresold ? Number(secondThresold) : undefined;
    const lastThresholdNum = lastThreshold ? Number(lastThreshold) : undefined;

    // if the value specified is less than any threshold values, consider it as an error
    if (
      (isValidConfigNumber(firstThreshold)
        && (deleteInactiveUsersAfterDaysNum <= firstThresholdNum))
      || (isValidConfigNumber(secondThresold)
        && (deleteInactiveUsersAfterDaysNum <= secondThresold))
      || (isValidConfigNumber(lastThreshold)
        && (deleteInactiveUsersAfterDaysNum <= lastThreshold))
    )
     {
      updatedErrorsMap.inactiveDays = true;
    }

    // if the value specified is less than second or last threshold, consider it as an eror
    if ((
      (isValidConfigNumber(secondThresold) && (firstThresholdNum <= secondThresoldNum))
      ||
      (isValidConfigNumber(lastThreshold) && (firstThresholdNum <= lastThreshold))
    )
      ||
      (firstThreshold && !secondThresold)
    ) {
      updatedErrorsMap.firstThreshold = true;
    }

    // if the value specified is less than last threshold, consider it as an eror
    if ((isValidConfigNumber(lastThreshold) && (secondThresoldNum <= lastThresholdNum))
      || (secondThresold && !lastThreshold)) {
      updatedErrorsMap.secondThresold = true;
    }

    setErrorsMap(updatedErrorsMap);
  }, [deleteInactiveUsersAfterDays, thresholdValues]);

  const getThresholdError = (type) => {
    if (type === TYPE_THRESHOLD_FIRST) {
      return errorsMap.firstThreshold;
    } else if (type === TYPE_THRESHOLD_SECOND) {
      return errorsMap.secondThresold;
    } else if (type === TYPE_THRESHOLD_LAST) {
      return errorsMap.lastThreshold;
    }
    return false;
  };

  const renderItem =
    ({ prefix, showInputField = true, suffix, title, type }) => {
      const thresholdIndex = getThresholdIndex(type);
      const error = getThresholdError(type);
      const hasThresholdValue = type !== TYPE_THRESHOLD_ACTIVE;
      const itemTextClass = type !== TYPE_THRESHOLD_ACTIVE ?
        classes.thresholdListItemText : classes.activeThresholdListItemText;
      const showClearBtn = type !== TYPE_THRESHOLD_LAST;
      return (
        <ListItem className={classes.thresholdListItem}>
          <ListItemIcon
            classes={{
              root: getThresholdItemIcon(type),
            }}
          >
            <FiberManualRecordSharpIcon
              style={{ fontSize: 16 }}
            />
          </ListItemIcon>
          <ListItemText
            classes={{
              primary: itemTextClass,
            }}
          >
            <span className={classes.listItemTitle}>{title}</span>
            &nbsp;
            {prefix}
            {showInputField &&
              <FormTextField
                error={error}
                fieldContainerClass={classes.thresholdCategoryField}
                handleChange={(value) => handleThresholdChange(thresholdIndex, value)}
              suffixIcon={showClearBtn && <Clear color={"primary"} onClick={() => handleThresholdChange(thresholdIndex, '')}/>}
                textFieldClass={classes.thresholdTextField}
                variant="standard"
                value={hasThresholdValue && thresholdValues[thresholdIndex]}
              />
            }
            {suffix}
          </ListItemText>
        </ListItem>
      );
    };

  const handleNotificationEnabled = (value) => {
    setRegistrationSettingsState({
      ...registrationSettingsState,
      notificationEnabled : value,
    });
  };

  const handleRemoveAdminUsers = (event) => {
    const checked = get(event, 'target.checked', false);
    setRegistrationSettingsState({
      ...registrationSettingsState,
      removeAdminUsers : checked,
    });
  };

  const handleDeleteInactiveUsersAfterDays = (value) => {
    setRegistrationSettingsState({
      ...registrationSettingsState,
      deleteInactiveUsersAfter: value,
    });
  };

  const renderRegistrationSettings = () =>
    <div className={classes.settingsSection}>
      <Setting
        handleChange={onRegistrationSwitchChange}
        id={'registration-settings-registration-switch'}
        helperText={getI18nMessage(`label.settings.registrationsettings.registration.title.helpertext`)}
        title={getI18nMessage(`label.settings.registrationsettings.registration.title`)}
        value={registrationSettingsState["registrationStatus"] === REGISTRATION_SETTINGS_STATUS_ENABLED}
      />
      {registrationSettingsState["registrationStatus"] === REGISTRATION_SETTINGS_STATUS_ENABLED
        && renderRegistrationWorkflow()}
      <Setting
        handleChange={handleDeleteInactiveUsers}
        helperText={getI18nMessage(`label.settings.registrationsettings.delete.inactive.users.title.helpertext`)}
        id={'registration-settings-inactive-users-switch'}
        title={getI18nMessage(`label.settings.registrationsettings.delete.inactive.users.title`)}
        value={deleteInactiveUsersAfter}
      />
      {deleteInactiveUsersAfter && <div>
        <FormTextField
          error={errorsMap.inactiveDays}
          handleChange={handleDeleteInactiveUsersAfterDays}
          helperText={getI18nMessage('label.settings.registrationsettings.days.before.deletion.helpertext')}
          name={getI18nMessage('label.settings.registrationsettings.days.before.deletion.title')}
          type={'number'}
          variant="standard"
          value={deleteInactiveUsersAfterDays}
        />
        <ListItem className={clsx(classes.listItem, classes.removeAdminUsers)}>
          <ListItemIcon className={classes.listItemIcon}>
            <Checkbox
              id="registration-settings-remove-admin-checkbox"
              size="small"
              edge="start"
              disableRipple
              inputProps={{
                'aria-label': 'primary checkbox',
              }}
              onClick={handleRemoveAdminUsers}
              checked={registrationSettingsState.removeAdminUsers}
            />
          </ListItemIcon>
          <ListItemText
            className={classes.listItemText}
            primary={getI18nMessage('label.settings.registrationsettings.exclude.admin.users.title')}
            primaryTypographyProps={{ className : classes.listItemTextPrimary }}
            secondary={getI18nMessage('label.settings.registrationsettings.exclude.admin.users.title.helpertext')}
            secondaryTypographyProps={{ className : classes.listItemTextSecondary }}
          />
        </ListItem>
        <Typography variant="h4" className={classes.switchTitle}>
          {getI18nMessage('label.settings.registrationsettings.inactivity.thresholds.title')}
        </Typography>
        <div className={classes.thresholdsHelperText}>
          {getI18nMessage('label.settings.registrationsettings.inactivity.thresholds.helpertext')}
        </div>
        <List>
          {renderItem({
            prefix: getI18nMessage('label.settings.registrationsettings.threshold.active.prefix'),
            showInputField: false,
            type: TYPE_THRESHOLD_ACTIVE,
          })}
          {renderItem({
            prefix: getI18nMessage('label.settings.registrationsettings.threshold.prefix'),
            suffix: getI18nMessage('label.settings.registrationsettings.threshold.suffix.first'),
            title: getI18nMessage('label.settings.registrationsettings.threshold.title.first'),
            type: TYPE_THRESHOLD_FIRST,
          })}
          {renderItem({
            prefix: getI18nMessage('label.settings.registrationsettings.threshold.prefix'),
            suffix: getI18nMessage('label.settings.registrationsettings.threshold.suffix.second'),
            title: getI18nMessage('label.settings.registrationsettings.threshold.title.second'),
            type: TYPE_THRESHOLD_SECOND,
          })}
          {renderItem({
            prefix: getI18nMessage('label.settings.registrationsettings.threshold.prefix'),
            suffix: getI18nMessage('label.settings.registrationsettings.threshold.suffix.last'),
            title: getI18nMessage('label.settings.registrationsettings.threshold.title.last'),
            type: TYPE_THRESHOLD_LAST,
          })}
        </List>
        <Setting
          handleChange={handleNotificationEnabled}
          helperText={getI18nMessage(`label.settings.registrationsettings..notify.user.title.helpertext`)}
          id={'registration-settings-notifications-switch'}
          title={getI18nMessage(`label.settings.registrationsettings.notify.user.title`)}
          value={registrationSettingsState.notificationEnabled}
        />
      </div>}
    </div>;

  const renderRegistrationWorkflow = () =>
    <>
      {/*Limit User Registration Checkbox */}
      <ListItem
        className={classes.listItem}
      >
        <ListItemIcon className={classes.listItemIcon}>
          <FormControlLabel
            control={
              <Checkbox
                id="registration-settings-limit-user-checkbox"
                data-apim-test="registration-settings-limit-user-checkbox"
                checked={registrationSettingsState.userRegistrationLimitEnabled}
                onChange={(e) => onClickLimitUserRegistration(e)}
                inputProps={{
                  'aria-label': 'primary checkbox',
                }}
                size="small"
                edge="start"
                disableRipple
              />
            }
          />
        </ListItemIcon>
        <ListItemText
          className={classes.listItemText}
          primary={getI18nMessage("label.settings.registrationsettings.limituser.title")}
          primaryTypographyProps={{ className : classes.listItemTextPrimary }}
          secondary={getI18nMessage("label.settings.registrationsettings.limituser.subtitle")}
          secondaryTypographyProps={{ className : classes.listItemTextSecondary }}
        />
      </ListItem>

      {/*Terms of Use Text Field*/}
      <div className={classes.textFieldContainer}>
        <FormTextField
          id={'registration-settings-termsofuse-textfield'}
          name={getI18nMessage('label.settings.registrationsettings.registration.termsOfUse')}
          value={parseNewLine(registrationSettingsState.termOfUse)}
          handleChange={onTermsOfUseChange}
          helperText={
            getI18nMessage('label.settings.registrationsettings.registration.termsOfUse.helpertext')
            + ` Maximum length is ${REGISTRATION_SETTINGS_TERMS_OF_USE_MAX_LENGTH} characters.`
          }
          defaultValue={parseNewLine(registrationSettingsState.termOfUse)}
          multiline={true}
          rows={12}
          maxLength={REGISTRATION_SETTINGS_TERMS_OF_USE_MAX_LENGTH}
        />
      </div>
    </>;

  const hasErrors = keys(errorsMap).length > 0;

  return (
    <div>
      {isLoading ? (
        <Loading />
      ) : (
        <div className={classes.registrationSettingsContainer}>
          <div className={classes.details}>
            <Typography variant="h6" className={classes.smallLabel}>
              {getI18nMessage("label.settings.title")}
            </Typography>
            <Typography variant="h1" className={classes.pageTitle}>
              {getI18nMessage("label.settings.registrationsettings.title")}
            </Typography>
            {renderRegistrationSettings()}
          </div>
          { isFormDirty && <FormActionButtons
            isContainerFooter={true}
            id={"registration-settings-footer-buttons"}
            cancelText={getI18nMessage("label.cancel.button")}
            nextText={getI18nMessage("label.save.button")}
            onCancelClick={onCancel}
            onNextClick={onSave}
            isDisabled={hasErrors}
          />}
        </div>
      )}
      {notificationMessage!=='' && (
        <AlertMessages
          id={'registration-settings-alert-messages'}
          variant={notificationStatus}
          message={notificationMessage}
          containerClass={clsx(
            classes.notificationContainerRegistrationSettings,
            classes.notificationWidthClass,
          )}
          onClose={() => notifyMessages('', '')}
          hasNotificationIcon={true}
        />
      )}
      <Footer />
    </div>
  );
};
const mapStateToProps = (state) => ({
  registrationSettings: getRegistrationSettings(state),
  isLoading: getIsLoading(state),
  isUpdateError: getIsFormUpdateError(state),
  isUpdateSuccessful: getIsUpdateSuccessful(state),
  isFormLoadingError: getIsFormLoadingError(state),
  updateErrorMsg: getUpdateErrorMsg(state),
  user: getUserDetails(state),
});

const mapDispatchToProps = {
  fetchRegistrationSettings,
  updateRegistrationSettings,
  push,
};

RegistrationSettings.propTypes = {
  registrationSettings: PropTypes.object,
  updateRegistrationSettings: PropTypes.func,
  isLoading: PropTypes.bool,
  isFormLoadingError: PropTypes.bool,
  isUpdateError: PropTypes.bool,
  updateErrorMsg: PropTypes.string,
  isUpdateSuccessful: PropTypes.bool,
  fetchRegistrationSettings: PropTypes.func,
  user: PropTypes.object,
  push: PropTypes.func,
  classes: PropTypes.object,
};

RegistrationSettings.defaultProps = {
  registrationSettings : {
    termOfUse: REGISTRATION_SETTINGS_TERMS_OF_USE_DEFAULT,
    registrationStatus: REGISTRATION_SETTINGS_STATUS_DISABLED,
    userRegistrationLimitEnabled: REGISTRATION_SETTINGS_LIMIT_DISABLED,
    userRegistrationLimitId: REGISTRATION_SETTINGS_LIMIT_ID_DEFAULT,
  },
}

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps))(RegistrationSettings);
